import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { impersonationSession, useAuth } from '../lib/auth';
import type { LicenseInfo } from '../lib/license';

interface Ann {
  id: string;
  title: string;
  body: string;
  level: 'info' | 'warning' | 'critical';
}
const tone = {
  info: 'border-brand-200 bg-brand-50 text-brand-800',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  critical: 'border-red-300 bg-red-50 text-red-900',
} as const;
const KEY = 'admin.ann.dismissed';
const loadDismissed = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
};

/** Pengumuman dari Super Admin (bisa ditutup per pengguna; yang "penting" tidak bisa ditutup). */
export function AnnouncementBanner() {
  const { data } = useQuery({
    queryKey: ['announcements'],
    refetchInterval: 5 * 60_000,
    queryFn: () => api.get<Ann[]>('/announcements'),
  });
  const [gone, setGone] = useState<string[]>(loadDismissed);
  const list = (data ?? []).filter((a) => a.level === 'critical' || !gone.includes(a.id));
  if (list.length === 0) return null;
  return (
    <div className="space-y-2 border-b bg-surface px-4 py-2">
      {list.map((a) => (
        <div
          key={a.id}
          role="status"
          className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${tone[a.level]}`}
        >
          <span>
            <b>{a.title}</b> — {a.body}
          </span>
          {a.level !== 'critical' && (
            <button
              className="shrink-0 text-xs underline"
              onClick={() => {
                const next = [...gone, a.id];
                setGone(next);
                try {
                  localStorage.setItem(KEY, JSON.stringify(next));
                } catch {
                  /* abaikan */
                }
              }}
            >
              Tutup
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** Penanda tegas sesi dukungan Super Admin + hitung mundur; "Akhiri" menghapus token tab ini. */
export function SupportBanner() {
  const { me, logout } = useAuth();
  const imp = impersonationSession();
  const [left, setLeft] = useState(() => (imp ? imp.exp - Date.now() : 0));
  useEffect(() => {
    if (!imp) return;
    const t = setInterval(() => {
      const l = imp.exp - Date.now();
      setLeft(l);
      if (l <= 0) void logout();
    }, 1000);
    return () => clearInterval(t);
  }, [imp, logout]);
  if (!me?.impersonation || !imp) return null;
  const m = Math.max(0, Math.floor(left / 60_000));
  const s = Math.max(0, Math.floor((left % 60_000) / 1000));
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-2 bg-warning-700 px-4 py-2 text-sm font-medium text-white"
    >
      <span>
        MODE DUKUNGAN SUPER ADMIN · {imp.tenantName} · berakhir dalam {m}:
        {String(s).padStart(2, '0')} · semua aksi tercatat atas nama Super Admin
      </span>
      <button
        className="rounded bg-surface/20 px-3 py-1 hover:bg-surface/30"
        onClick={() => {
          void logout().then(() => window.close());
        }}
      >
        Akhiri sesi
      </button>
    </div>
  );
}

/** Pengingat masa uji coba: tampil ≤ 7 hari sebelum berakhir dan setelah berakhir (tidak bisa ditutup bila sudah habis). */
export function TrialBanner() {
  const { can } = useAuth();
  const { data } = useQuery({
    queryKey: ['subscription-banner'],
    enabled: can('subscription:read'),
    refetchInterval: 60 * 60_000,
    queryFn: () => api.get<{ status: string; trialEndsAt: string | null }>('/subscription'),
  });
  if (!data || data.status !== 'trial' || !data.trialEndsAt) return null;
  const days = Math.ceil((Date.parse(data.trialEndsAt) - Date.now()) / 86_400_000);
  if (days > 7) return null;
  const expired = days <= 0;
  return (
    <div
      role={expired ? 'alert' : 'status'}
      className={`flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2 text-sm ${expired ? tone.critical : tone.warning}`}
    >
      <span>
        {expired
          ? 'Masa uji coba sudah berakhir. Hubungi kami untuk mengaktifkan paket agar layanan tidak dihentikan.'
          : `Masa uji coba berakhir dalam ${days} hari. Hubungi kami untuk mengaktifkan paket berbayar.`}
      </span>
      <Link to="/office/langganan" className="font-medium underline">
        Lihat langganan
      </Link>
    </div>
  );
}

/**
 * Pengingat lisensi (self-hosted): masa update ≤ 30 hari atau sudah berakhir. Aplikasi TETAP berjalan penuh setelah masa
 * update berakhir; yang tidak bisa hanya memasang versi baru.
 */
export function LicenseBanner() {
  const { can } = useAuth();
  const { data } = useQuery({
    queryKey: ['license'],
    enabled: can('subscription:read'),
    retry: false,
    refetchInterval: 60 * 60_000,
    queryFn: () => api.get<LicenseInfo | { selfHosted: false }>('/license'),
  });
  if (!data || !data.selfHosted) return null;
  if (data.updatesActive && data.updatesDaysLeft > 30) return null;
  const date = new Date(`${data.updatesUntil}T00:00:00`).toLocaleDateString('id-ID', {
    dateStyle: 'long',
  });
  return (
    <div
      role="status"
      className={`flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2 text-sm ${data.updatesActive ? tone.warning : tone.info}`}
    >
      <span>
        {data.updatesActive
          ? `Masa update lisensi berakhir ${date} (${data.updatesDaysLeft} hari lagi). Aplikasi tetap berfungsi, tetapi versi baru tidak dapat dipasang setelah itu.`
          : `Masa update lisensi berakhir ${date}. Aplikasi tetap berfungsi penuh; hubungi penjual untuk memperpanjang agar bisa memasang versi baru.`}
      </span>
      <Link to="/office/langganan" className="font-medium underline">
        Lihat lisensi
      </Link>
    </div>
  );
}
